import React, { useState, useEffect } from 'react';
import {
  makeStyles, mergeClasses, tokens,
  Dialog, DialogSurface, DialogBody, DialogTitle, DialogContent, DialogActions,
  Button, Spinner, Text,
} from '@fluentui/react-components';
import { iconMap } from '../../theme/icons';
import SelectionBox from './../components/blocks/SelectionBox';
import { API_HOST } from '../../config.js';

const CloseIcon = iconMap.close;

// Pagalbinė funkcija paveiksliuko URL
const getImageUrl = (imageName) => {
  if (!imageName) return null;
  // Jei tai pilnas URL (pvz. išoriniai šaltiniai)
  if (imageName.startsWith('http')) return imageName;
  // Jei tai failas iš jūsų serverio uploads
  return `${API_HOST}/uploads/${imageName}`;
};

const useStyles = makeStyles({
  surface: { maxWidth: '75rem', width: '100%' },
  loadingWrap: { display: 'flex', justifyContent: 'center', padding: tokens.spacingVerticalXXL },
  errorText: { color: tokens.colorStatusDangerForeground1 },
  // CSS grid + repeat(auto-fill,...) su fiksuoto pločio SelectionBox (type=column,
  // justifySelf:start) palikdavo tuščius grid langelius kaip pilno pločio "tarpus",
  // kai stulpelių daugiau nei realių elementų (žr. /admin/bugs "Ta pati grid
  // problema, nenaudojam grid" — bendra taisyklė visiems dialogams). flex+wrap+gap
  // vietoj grid — kiekvienas elementas užima tik savo turinio plotį.
  grid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    marginTop: tokens.spacingVerticalS,
  },
  itemPrice: { textAlign: 'center', marginTop: tokens.spacingVerticalXS, display: 'block' },

  // Pasirenkamas "vietos" pasirinkimas (pvz. veidrodžio Nėra/Išorėje/Viduje) — rodomas
  // TIK kai `placementOptions` paduota, virš prekių sąrašo. Anksčiau šis pasirinkimas
  // gyveno kaip atskira pilių eilutė lentelėje, ŠALIA dialogo — vartotojas paprašė, kad
  // jis būtų PAČIAME dialogo lange (žr. /admin/bugs "Sie pasirinkimai turi buti
  // veidrodzio dialog lange").
  placementRow: { display: 'flex', gap: tokens.spacingHorizontalXS, marginBottom: tokens.spacingVerticalM, flexWrap: 'wrap' },
  placementPill: {
    paddingLeft: tokens.spacingHorizontalM, paddingRight: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalSNudge, paddingBottom: tokens.spacingVerticalSNudge,
    borderRadius: tokens.borderRadiusMedium, border: `1px solid ${tokens.colorNeutralStroke2}`, cursor: 'pointer',
    fontSize: tokens.fontSizeBase300, fontWeight: tokens.fontWeightMedium,
    transitionProperty: 'all', transitionDuration: '0.12s',
    color: tokens.colorNeutralForeground3, backgroundColor: tokens.colorNeutralBackground1,
  },
  placementPillActive: { borderColor: tokens.colorBrandStroke1, backgroundColor: tokens.colorBrandBackground2, color: tokens.colorBrandForeground2 },
  placementHint: { color: tokens.colorNeutralForeground3, display: 'block', padding: `${tokens.spacingVerticalXL} 0`, textAlign: 'center' },
});

const UniversalSelectionDialog = ({
  open,
  onClose,
  onSelect,
  title,
  apiUrl, // pvz: `${API_BASE}/mirrors`
  type, // 'mirror', 'filling', 'handle' (kad žinotume kokius laukus atvaizduoti)
  // Pasirenkamas "vietos" pasirinkimas (pvz. veidrodžio Nėra/Išorėje/Viduje) — kai
  // paduota, pilių eilutė rodoma dialogo viršuje, o prekių sąrašas paslepiamas, kol
  // pasirinkta reikšmė yra 'none' (nėra ką rinktis, kol nepasakyta KUR dėti prekę).
  placementOptions, placementValue, onPlacementChange,
}) => {
  const s = useStyles();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hideGrid = !!placementOptions && placementValue === 'none';

  useEffect(() => {
    if (open && apiUrl) {
      setLoading(true);
      fetch(apiUrl)
        .then(res => {
          if (!res.ok) throw new Error('Nepavyko gauti duomenų');
          return res.json();
        })
        .then(data => {
          setItems(data);
          setLoading(false);
        })
        .catch(err => {
          console.error(err);
          setError(err.message);
          setLoading(false);
        });
    }
  }, [open, apiUrl]);

  return (
    <Dialog open={open} onOpenChange={(_e, data) => { if (!data.open) onClose(); }}>
      <DialogSurface id="dialog-universal-selection" className={s.surface}>
        <DialogBody>
          <DialogTitle action={<Button appearance="subtle" icon={<CloseIcon />} aria-label="Uždaryti" onClick={onClose} />}>
            {title}
          </DialogTitle>
          <DialogContent>
            {placementOptions && (
              <div className={s.placementRow}>
                {placementOptions.map((opt) => (
                  <div
                    key={opt.value}
                    onClick={() => onPlacementChange?.(opt.value)}
                    className={mergeClasses(s.placementPill, placementValue === opt.value && s.placementPillActive)}
                  >{opt.label}</div>
                ))}
              </div>
            )}
            {hideGrid ? (
              <Text className={s.placementHint}>Pasirinkite vietą aukščiau, kad pamatytumėte pasirinkimų sąrašą.</Text>
            ) : loading ? (
              <div className={s.loadingWrap}><Spinner /></div>
            ) : error ? (
              <Text className={s.errorText}>{error}</Text>
            ) : (
              <div className={s.grid}>
                {items.map((item) => (
                  <div key={item.id || item._id}>
                    <SelectionBox
                      // Bandome įvairius paveiksliukų laukus
                      image={getImageUrl(item.image_1 || item.image || item.imagePath)}
                      title={item.code || item.name}
                      subTitle={item.name || item.color}
                      onClick={() => onSelect(item)}
                    />
                    <Text size={200} className={s.itemPrice}>Kaina: {item.price} €</Text>
                  </div>
                ))}
              </div>
            )}
          </DialogContent>
          <DialogActions>
            <Button size="large" appearance="outline" onClick={onClose}>Uždaryti</Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default UniversalSelectionDialog;
